/**
 * One list, filtered as you type, chosen with the keyboard.
 *
 * TWO KEYS NEEDED THIS AND NEITHER COULD HAVE IT. `l` (label) and `v` (move to)
 * were declared in the keymap, resolved to no command, and were DELETED with the
 * reason written into `keymap.ts`: both need somewhere to answer "which one",
 * and a key that opens nothing is worse than an absent key. This is that
 * somewhere, and it is one component rather than two because the question is the
 * same — a list of names, most of them already used, and sometimes a new one.
 *
 * IT IS NOT A `<datalist>` OR A `<select>`. A select cannot be typed into, and a
 * datalist is a browser-drawn popup with no keyboard contract worth relying on:
 * Safari and Firefox disagree about whether Enter picks the highlighted row, and
 * neither will tell a screen reader what is highlighted. This draws the list, so
 * `aria-activedescendant` can name the row a person is on and Enter can mean
 * exactly one thing.
 *
 * THE FILTER IS A SUBSTRING AND NOT A PREFIX. `Finance/Bank` is found by typing
 * `bank`, which is what somebody looking for it types — nested mailbox names put
 * the distinguishing word last, and a prefix match makes the whole hierarchy
 * unreachable from the keyboard.
 *
 * ESCAPE CLOSES AND CHANGES NOTHING, and the scrim does the same. This is a
 * question, not a step: a reader who opened it by mistyping `v` gets out with
 * one key and no mail has moved.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'

export interface PickerOption {
  /** What is sent. */
  readonly value: string
  /** What is shown. Defaults to `value`. */
  readonly label?: string
  /** A quiet second column — a count, a destination, whatever fits. */
  readonly detail?: string
}

export interface PickerProps {
  readonly title: string
  readonly placeholder: string
  readonly options: readonly PickerOption[]
  /**
   * Whether typing something that is not on the list is an answer.
   *
   * TRUE FOR LABELS AND FALSE FOR MAILBOXES, which is the whole difference
   * between the two callers. A label is a word somebody invents — the first one
   * is always new — while a mailbox that does not exist is a `UID MOVE` the mail
   * server answers NO to, so offering it would be offering a refusal.
   */
  readonly allowNew?: boolean
  readonly onChoose: (value: string) => void
  readonly onClose: () => void
}

export function Picker({
  title,
  placeholder,
  options,
  allowNew = false,
  onChoose,
  onClose,
}: PickerProps): React.JSX.Element {
  const ids = useId()
  const [typed, setTyped] = useState('')
  const [cursor, setCursor] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  /** Where focus goes back to. A dialog that steals it and never returns it is a trap. */
  const returnTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    input.current?.focus()
    return () => returnTo.current?.focus()
  }, [])

  const matches = useMemo(() => {
    const needle = typed.trim().toLowerCase()
    const found = options.filter(
      (option) => needle === '' || (option.label ?? option.value).toLowerCase().includes(needle),
    )
    // THE NEW ONE IS OFFERED LAST AND ONLY WHEN IT IS NEW. Above the list it
    // would be the default answer, and the default answer for a label control
    // must be the label you already have — three spellings of `invoices` is
    // exactly what a picker exists to prevent.
    const exact = options.some((option) => option.value.toLowerCase() === needle)
    return allowNew && needle !== '' && !exact
      ? [...found, { value: typed.trim(), detail: 'new' }]
      : found
  }, [allowNew, options, typed])

  // THE CURSOR IS CLAMPED, NOT REMEMBERED. Typing a letter shortens the list,
  // and a cursor left pointing past the end makes Enter do nothing at all —
  // which reads as a broken key rather than as an empty list.
  const at = Math.min(cursor, Math.max(0, matches.length - 1))
  const chosen = matches[at]

  return (
    <div
      className="settings-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="picker" role="dialog" aria-modal="true" aria-label={title}>
        <label className="picker-field" htmlFor={`${ids}-input`}>
          <span className="picker-title">{title}</span>
          <input
            id={`${ids}-input`}
            ref={input}
            type="text"
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded="true"
            aria-controls={`${ids}-list`}
            aria-activedescendant={chosen ? `${ids}-option-${at}` : undefined}
            placeholder={placeholder}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value)
              setCursor(0)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setCursor((current) => Math.min(current + 1, matches.length - 1))
              } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                setCursor((current) => Math.max(current - 1, 0))
              } else if (event.key === 'Enter') {
                event.preventDefault()
                if (chosen) onChoose(chosen.value)
              } else if (event.key === 'Escape') {
                // STOPPED HERE, so it closes this and not the panel behind it.
                // A reader who opens a picker over the reading pane and presses
                // Escape means the picker.
                event.preventDefault()
                event.stopPropagation()
                onClose()
              }
            }}
          />
        </label>

        <ul className="picker-list" id={`${ids}-list`} role="listbox" aria-label={title}>
          {matches.length === 0 ? (
            <li className="picker-empty">Nothing matches “{typed.trim()}”.</li>
          ) : (
            matches.map((option, index) => (
              <li
                key={option.value}
                id={`${ids}-option-${index}`}
                role="option"
                aria-selected={index === at}
                className="picker-option"
                data-at={index === at ? 'yes' : 'no'}
              >
                <button
                  type="button"
                  className="picker-choose"
                  // THE MOUSE AND THE KEYBOARD AGREE. Hovering moves the cursor,
                  // so the row Enter would take is the row under the pointer —
                  // two highlights on one list is a control that has to be read
                  // twice before it can be used once.
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => onChoose(option.value)}
                >
                  <span className="picker-name">{option.label ?? option.value}</span>
                  {option.detail ? <span className="picker-detail">{option.detail}</span> : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}
